import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Headphones, Zap, DollarSign, Volume2, Settings } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { authFetch } from '@/utils/auth-fetch';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface EnhancedVoiceSettings {
  enabled: boolean;
  mode: 'none' | 'push_to_talk' | 'realtime';
  voice: {
    provider: 'openai' | 'elevenlabs' | 'google' | 'azure';
    voiceId: string;
    language: string;
    speed: number;
    pitch: number;
    volume: number;
  };
  pushToTalk: {
    buttonPosition: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'inline';
    buttonStyle: 'circle' | 'rounded' | 'square';
    buttonSize: 'small' | 'medium' | 'large';
    holdToTalk: boolean;
    showVisualFeedback: boolean;
    playSound: boolean;
  };
  realtime: {
    wakeWord: string;
    enableWakeWord: boolean;
    continuousListening: boolean;
    silenceTimeout: number;
    interruptionHandling: 'stop' | 'pause' | 'continue';
    backgroundNoiseFilter: boolean;
  };
  advanced: {
    customPrompts: boolean;
    systemPrompt?: string;
    maxResponseLength: number;
    streamingEnabled: boolean;
    fallbackToText: boolean;
    errorHandling: 'silent' | 'message' | 'retry';
  };
}

interface EnhancedVoiceSettingsProps {
  siteId: string;
  isAdmin?: boolean;
}

const VOICE_OPTIONS = {
  openai: [
    { id: 'alloy', name: 'Alloy', gender: 'Neutral' },
    { id: 'echo', name: 'Echo', gender: 'Male' },
    { id: 'fable', name: 'Fable', gender: 'Male' },
    { id: 'onyx', name: 'Onyx', gender: 'Male' },
    { id: 'nova', name: 'Nova', gender: 'Female' },
    { id: 'shimmer', name: 'Shimmer', gender: 'Female' },
  ],
  elevenlabs: [
    { id: 'rachel', name: 'Rachel', gender: 'Female' },
    { id: 'drew', name: 'Drew', gender: 'Male' },
    { id: 'clyde', name: 'Clyde', gender: 'Male' },
    { id: 'paul', name: 'Paul', gender: 'Male' },
    { id: 'domi', name: 'Domi', gender: 'Female' },
    { id: 'dave', name: 'Dave', gender: 'Male' },
    { id: 'fin', name: 'Fin', gender: 'Male' },
    { id: 'bella', name: 'Bella', gender: 'Female' },
  ],
};

const LANGUAGES = [
  { code: 'en-US', name: 'English (US)' },
  { code: 'en-GB', name: 'English (UK)' },
  { code: 'es-ES', name: 'Spanish (Spain)' },
  { code: 'es-MX', name: 'Spanish (Mexico)' },
  { code: 'fr-FR', name: 'French' },
  { code: 'de-DE', name: 'German' },
  { code: 'it-IT', name: 'Italian' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)' },
  { code: 'nl-NL', name: 'Dutch' },
  { code: 'ja-JP', name: 'Japanese' },
  { code: 'zh-CN', name: 'Chinese (Simplified)' },
  { code: 'ko-KR', name: 'Korean' },
];

export function EnhancedVoiceSettings({ siteId, isAdmin = false }: EnhancedVoiceSettingsProps) {
  const [settings, setSettings] = useState<EnhancedVoiceSettings>({
    enabled: false,
    mode: 'none',
    voice: {
      provider: 'openai',
      voiceId: 'nova',
      language: 'en-US',
      speed: 1.0,
      pitch: 1.0,
      volume: 0.8,
    },
    pushToTalk: {
      buttonPosition: 'bottom-right',
      buttonStyle: 'circle',
      buttonSize: 'medium',
      holdToTalk: true,
      showVisualFeedback: true,
      playSound: true,
    },
    realtime: {
      wakeWord: 'Hey PageMind',
      enableWakeWord: false,
      continuousListening: true,
      silenceTimeout: 2000,
      interruptionHandling: 'pause',
      backgroundNoiseFilter: true,
    },
    advanced: {
      customPrompts: false,
      maxResponseLength: 500,
      streamingEnabled: true,
      fallbackToText: true,
      errorHandling: 'message',
    },
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
  }, [siteId]);

  const loadSettings = async () => {
    try {
      const response = await authFetch(`/sites/${siteId}/settings`);
      if (response.ok) {
        const data = await response.json();
        const voiceSettings = data.settings?.voice;
        if (voiceSettings) {
          // Map old settings to new structure
          const mode = voiceSettings.voice_realtime_enabled ? 'realtime' : 
                      voiceSettings.voice_push_to_talk_enabled ? 'push_to_talk' : 'none';
          
          setSettings(prev => ({
            ...prev,
            enabled: voiceSettings.voiceEnabled || false,
            mode,
            ...voiceSettings.enhanced,
          }));
        }
      }
    } catch (error) {
      console.error('Error loading voice settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load voice settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`/sites/${siteId}/settings`, {
        method: 'PUT',
        body: JSON.stringify({ 
          settings: { 
            voice: {
              voiceEnabled: settings.enabled,
              voice_push_to_talk_enabled: settings.mode === 'push_to_talk',
              voice_realtime_enabled: settings.mode === 'realtime',
              enhanced: settings,
            }
          } 
        }),
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Voice settings updated successfully',
        });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving voice settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save voice settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleVoiceSettingChange = (key: keyof typeof settings.voice, value: any) => {
    setSettings(prev => ({
      ...prev,
      voice: { ...prev.voice, [key]: value }
    }));
  };

  const handlePushToTalkChange = (key: keyof typeof settings.pushToTalk, value: any) => {
    setSettings(prev => ({
      ...prev,
      pushToTalk: { ...prev.pushToTalk, [key]: value }
    }));
  };

  const handleRealtimeChange = (key: keyof typeof settings.realtime, value: any) => {
    setSettings(prev => ({
      ...prev,
      realtime: { ...prev.realtime, [key]: value }
    }));
  };

  const handleAdvancedChange = (key: keyof typeof settings.advanced, value: any) => {
    setSettings(prev => ({
      ...prev,
      advanced: { ...prev.advanced, [key]: value }
    }));
  };

  if (loading) {
    return <div>Loading voice settings...</div>;
  }

  const availableVoices = VOICE_OPTIONS[settings.voice.provider] || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Headphones className="h-5 w-5" />
          Enhanced Voice Assistant Settings
        </CardTitle>
        <CardDescription>
          Configure advanced voice interaction settings for your site
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Voice Mode Selection */}
        <RadioGroup 
          value={settings.mode} 
          onValueChange={(value) => setSettings(prev => ({ ...prev, mode: value as any, enabled: value !== 'none' }))}
        >
          {/* None Option */}
          <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-accent/50 transition-colors">
            <RadioGroupItem value="none" id="voice-none" className="mt-1" />
            <Label htmlFor="voice-none" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-2 font-medium">
                <MicOff className="h-4 w-4" />
                Disabled
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                No voice interaction - text chat only
              </p>
            </Label>
          </div>

          {/* Push to Talk Option */}
          <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-accent/50 transition-colors">
            <RadioGroupItem value="push_to_talk" id="voice-ptt" className="mt-1" />
            <Label htmlFor="voice-ptt" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-2 font-medium">
                <Mic className="h-4 w-4" />
                Push-to-Talk
                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                  Recommended
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Click and hold the microphone button to speak
              </p>
              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  ~$0.10/min
                </span>
                <span>Whisper + GPT-4 + Voice Synthesis</span>
              </div>
            </Label>
          </div>

          {/* Realtime Option */}
          <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-accent/50 transition-colors">
            <RadioGroupItem value="realtime" id="voice-realtime" className="mt-1" />
            <Label htmlFor="voice-realtime" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-2 font-medium">
                <Zap className="h-4 w-4" />
                Real-time Conversation
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                  Premium
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Natural conversation - always listening, instant responses
              </p>
              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  ~$0.30/min
                </span>
                <span>OpenAI Realtime API</span>
              </div>
            </Label>
          </div>
        </RadioGroup>

        {/* Voice Configuration (shown when voice is enabled) */}
        {settings.mode !== 'none' && (
          <>
            {/* Voice Selection */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center gap-2">
                <Volume2 className="h-4 w-4" />
                <Label>Voice Configuration</Label>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Voice Provider</Label>
                  <Select 
                    value={settings.voice.provider} 
                    onValueChange={(value) => handleVoiceSettingChange('provider', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
                      <SelectItem value="google" disabled>Google (Coming Soon)</SelectItem>
                      <SelectItem value="azure" disabled>Azure (Coming Soon)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Voice</Label>
                  <Select 
                    value={settings.voice.voiceId} 
                    onValueChange={(value) => handleVoiceSettingChange('voiceId', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableVoices.map(voice => (
                        <SelectItem key={voice.id} value={voice.id}>
                          {voice.name} ({voice.gender})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Language</Label>
                <Select 
                  value={settings.voice.language} 
                  onValueChange={(value) => handleVoiceSettingChange('language', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map(lang => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label>Speech Speed</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[settings.voice.speed]}
                      onValueChange={([value]) => handleVoiceSettingChange('speed', value)}
                      min={0.5}
                      max={2.0}
                      step={0.1}
                      className="flex-1"
                    />
                    <span className="w-12 text-sm">{settings.voice.speed}x</span>
                  </div>
                </div>
                
                <div>
                  <Label>Voice Pitch</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[settings.voice.pitch]}
                      onValueChange={([value]) => handleVoiceSettingChange('pitch', value)}
                      min={0.5}
                      max={2.0}
                      step={0.1}
                      className="flex-1"
                    />
                    <span className="w-12 text-sm">{settings.voice.pitch}x</span>
                  </div>
                </div>
                
                <div>
                  <Label>Volume</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[settings.voice.volume * 100]}
                      onValueChange={([value]) => handleVoiceSettingChange('volume', value / 100)}
                      min={0}
                      max={100}
                      step={5}
                      className="flex-1"
                    />
                    <span className="w-12 text-sm">{Math.round(settings.voice.volume * 100)}%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Push-to-Talk Settings */}
            {settings.mode === 'push_to_talk' && (
              <div className="space-y-4 border-t pt-4">
                <div className="flex items-center gap-2">
                  <Mic className="h-4 w-4" />
                  <Label>Push-to-Talk Settings</Label>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Button Position</Label>
                    <Select 
                      value={settings.pushToTalk.buttonPosition} 
                      onValueChange={(value) => handlePushToTalkChange('buttonPosition', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bottom-right">Bottom Right</SelectItem>
                        <SelectItem value="bottom-left">Bottom Left</SelectItem>
                        <SelectItem value="top-right">Top Right</SelectItem>
                        <SelectItem value="top-left">Top Left</SelectItem>
                        <SelectItem value="inline">Inline in Chat</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Button Style</Label>
                    <Select 
                      value={settings.pushToTalk.buttonStyle} 
                      onValueChange={(value) => handlePushToTalkChange('buttonStyle', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="circle">Circle</SelectItem>
                        <SelectItem value="rounded">Rounded Square</SelectItem>
                        <SelectItem value="square">Square</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Button Size</Label>
                  <RadioGroup 
                    value={settings.pushToTalk.buttonSize} 
                    onValueChange={(value) => handlePushToTalkChange('buttonSize', value)}
                  >
                    <div className="flex gap-4">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="small" id="size-small" />
                        <Label htmlFor="size-small">Small</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="medium" id="size-medium" />
                        <Label htmlFor="size-medium">Medium</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="large" id="size-large" />
                        <Label htmlFor="size-large">Large</Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="holdToTalk">Hold to Talk</Label>
                    <input
                      type="checkbox"
                      id="holdToTalk"
                      checked={settings.pushToTalk.holdToTalk}
                      onChange={(e) => handlePushToTalkChange('holdToTalk', e.target.checked)}
                      className="h-4 w-4"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="showVisualFeedback">Visual Feedback</Label>
                    <input
                      type="checkbox"
                      id="showVisualFeedback"
                      checked={settings.pushToTalk.showVisualFeedback}
                      onChange={(e) => handlePushToTalkChange('showVisualFeedback', e.target.checked)}
                      className="h-4 w-4"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="playSound">Play Sound Effects</Label>
                    <input
                      type="checkbox"
                      id="playSound"
                      checked={settings.pushToTalk.playSound}
                      onChange={(e) => handlePushToTalkChange('playSound', e.target.checked)}
                      className="h-4 w-4"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Real-time Settings */}
            {settings.mode === 'realtime' && (
              <div className="space-y-4 border-t pt-4">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  <Label>Real-time Settings</Label>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="enableWakeWord">Enable Wake Word</Label>
                      <p className="text-xs text-muted-foreground">Activate by saying wake word</p>
                    </div>
                    <input
                      type="checkbox"
                      id="enableWakeWord"
                      checked={settings.realtime.enableWakeWord}
                      onChange={(e) => handleRealtimeChange('enableWakeWord', e.target.checked)}
                      className="h-4 w-4"
                    />
                  </div>
                  
                  {settings.realtime.enableWakeWord && (
                    <div>
                      <Label htmlFor="wakeWord">Wake Word</Label>
                      <input
                        id="wakeWord"
                        type="text"
                        value={settings.realtime.wakeWord}
                        onChange={(e) => handleRealtimeChange('wakeWord', e.target.value)}
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="Hey PageMind"
                      />
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="continuousListening">Continuous Listening</Label>
                      <p className="text-xs text-muted-foreground">Keep listening after response</p>
                    </div>
                    <input
                      type="checkbox"
                      id="continuousListening"
                      checked={settings.realtime.continuousListening}
                      onChange={(e) => handleRealtimeChange('continuousListening', e.target.checked)}
                      className="h-4 w-4"
                    />
                  </div>
                  
                  <div>
                    <Label>Silence Timeout (ms)</Label>
                    <div className="flex items-center gap-4">
                      <Slider
                        value={[settings.realtime.silenceTimeout]}
                        onValueChange={([value]) => handleRealtimeChange('silenceTimeout', value)}
                        min={500}
                        max={5000}
                        step={100}
                        className="flex-1"
                      />
                      <span className="w-16 text-sm">{settings.realtime.silenceTimeout}ms</span>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Interruption Handling</Label>
                    <Select 
                      value={settings.realtime.interruptionHandling} 
                      onValueChange={(value) => handleRealtimeChange('interruptionHandling', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="stop">Stop Speaking</SelectItem>
                        <SelectItem value="pause">Pause & Resume</SelectItem>
                        <SelectItem value="continue">Continue Speaking</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="backgroundNoiseFilter">Background Noise Filter</Label>
                    <input
                      type="checkbox"
                      id="backgroundNoiseFilter"
                      checked={settings.realtime.backgroundNoiseFilter}
                      onChange={(e) => handleRealtimeChange('backgroundNoiseFilter', e.target.checked)}
                      className="h-4 w-4"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Advanced Settings (for admin users) */}
            {isAdmin && (
              <div className="space-y-4 border-t pt-4">
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  <Label>Advanced Settings</Label>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="streamingEnabled">Enable Streaming</Label>
                    <input
                      type="checkbox"
                      id="streamingEnabled"
                      checked={settings.advanced.streamingEnabled}
                      onChange={(e) => handleAdvancedChange('streamingEnabled', e.target.checked)}
                      className="h-4 w-4"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="fallbackToText">Fallback to Text on Error</Label>
                    <input
                      type="checkbox"
                      id="fallbackToText"
                      checked={settings.advanced.fallbackToText}
                      onChange={(e) => handleAdvancedChange('fallbackToText', e.target.checked)}
                      className="h-4 w-4"
                    />
                  </div>
                  
                  <div>
                    <Label>Error Handling</Label>
                    <Select 
                      value={settings.advanced.errorHandling} 
                      onValueChange={(value) => handleAdvancedChange('errorHandling', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="silent">Silent (Log Only)</SelectItem>
                        <SelectItem value="message">Show Error Message</SelectItem>
                        <SelectItem value="retry">Auto Retry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Max Response Length</Label>
                    <div className="flex items-center gap-4">
                      <Slider
                        value={[settings.advanced.maxResponseLength]}
                        onValueChange={([value]) => handleAdvancedChange('maxResponseLength', value)}
                        min={100}
                        max={2000}
                        step={50}
                        className="flex-1"
                      />
                      <span className="w-16 text-sm">{settings.advanced.maxResponseLength}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Cost Alert */}
        {settings.mode === 'realtime' && (
          <Alert>
            <Zap className="h-4 w-4" />
            <AlertDescription>
              <strong>Real-time mode:</strong> This mode uses approximately 3x more resources than push-to-talk. 
              Estimated cost: ~$0.30/minute of active conversation.
            </AlertDescription>
          </Alert>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={saveSettings} disabled={saving}>
            {saving ? 'Saving...' : 'Save Voice Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}